import {
  Dispatch,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import throttle from 'lodash.throttle'
import {
  MRT_ColumnDef,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ColumnPinningState,
} from 'material-react-table'
import { Description } from '@mui/icons-material'
import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { DocumentRow } from '@/types/documents'
import { ProjectGrid } from '@/types/projects'
import { Workflow, WorkflowState } from '@/types/workflows'
import useColumnSortingLogic from '@/hooks/useColumnSortingLogic'
import useContextMenuPosition from '@/hooks/useContextMenuPosition'
import useDocumentDashboardIds from '@/hooks/useDocumentDashboardIds'
import useDocumentGrid from '@/hooks/useDocumentGrid'
import useFetchMoreOnBottomReached from '@/hooks/useFetchMoreOnBottomReached'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useLocalStorage from '@/hooks/useLocalStorage'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import useOverlay from '@/hooks/useOverlay'
import useValidationNavList from '@/hooks/useValidationNavList'
import { useGetDocumentWorkflowStates } from '@/service-library/hooks/document-workflow-states'
import queryKeys from '@/service-library/query-keys'
import { getBaseGrid } from '@/utils/field'
import DocumentDialog from '@/components/document-dialog/DocumentDialog'
import { useDocumentFiltersContext } from '@/components/document-filters/DocumentFiltersProvider'
import InFlightDocumentsDialog from '@/components/project-tables/InFlightDocumentsDialog'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import { WebsocketData } from '@/components/notifications/useWebsocket'
import ValidationOverlay from '@/components/validation-overlay/ValidationOverlay'
import { useColumnOrderContext } from '@/components/workflows/ColumnOrderProvider'
import { useColumnSortingContext } from '@/components/workflows/ColumnSortingProvider'
import { useColumnVisibilityContext } from '@/components/workflows/ColumnVisibilityProvider'
import DocumentGridActionButtons from '@/components/workflows/DocumentGridActionButtons'
import DocumentGridActionsMenu from '@/components/workflows/DocumentGridActionsMenu'
import DocumentGridRowMenu from '@/components/workflows/DocumentGridRowMenu'
import { useSelectedGridsContext } from '@/components/workflows/SelectedGridsProvider'
import { useViewsContext } from '@/components/workflows/ViewsProvider'
import WorkflowStateDescriptionDialog from '@/components/workflow-state-page/WorkflowStateDescriptionDialog'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'
import DragonFillProvider, { useDragonFillContext } from './DragonFillProvider'
import useDocumentColumns from './useDocumentColumns'
import useDocumentRowsColumns from './useDocumentRowsColumns'
import SelectAllBanner from './SelectAllBanner'
import useGetRelevantWorkflowState from '@/hooks/useRelevantWorkflowState'

const ROW_HEIGHT = 32

export type BaseDashboardTableProps = {
  isFullscreen: boolean
  workflow: Workflow
  setIsFullscreen: Dispatch<SetStateAction<boolean>>
  state?: WorkflowState
}

export type DashboardTableProps = BaseDashboardTableProps & {
  columns: MRT_ColumnDef<Required<DocumentRow>>[]
  queryBy: 'rows' | 'documents'
}

function DashboardTable({
  isFullscreen,
  workflow,
  columns,
  queryBy,
  setIsFullscreen,
  state,
}: DashboardTableProps) {
  const queryClient = useQueryClient()
  const theme = useTheme()
  const documentIdState = useStatefulSearchParam({
    param: 'document_id',
  })
  const { project } = useProjectContext()
  const baseGrid = getBaseGrid(project.project_grids as ProjectGrid[])

  const getRelevantWorkflowState = useGetRelevantWorkflowState()

  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({})
  const [selectedAllLoaded, setSelectedAllLoaded] = useState(false)
  const [selectedAll, setSelectedAll] = useState(false)
  const [useOnlySelected, setUseOnlySelected] = useState(false)

  const {
    columnOrder,
    draggingColumn,
    handleColumnOrderChange,
    setColumnOrder,
    setDraggingColumn,
  } = useColumnOrderContext()
  const { sorting, handleSortingChange, resetColumnSorting, setSorting } =
    useColumnSortingContext()
  const {
    columnVisibility,
    handleColumnVisibilityChange,
    resetColumnVisibility,
  } = useColumnVisibilityContext()

  const [columnPinning, setColumnPinning] = useState<MRT_ColumnPinningState>({
    left: ['mrt-row-select', 'indicators'],
  })

  const [columnSizing, setColumnSizing] = useLocalStorage(
    `columns-sizing-${state ? state.id : project.id}`,
    {},
  )

  const documentationOverlay = useOverlay()
  const validationOverlay = useOverlay()

  useEffect(() => {
    if (!documentIdState.isInUrl && validationOverlay.isOpen) {
      validationOverlay.close()
    }
  }, [documentIdState.isInUrl, validationOverlay])

  function openValidationOverlay(documentId: string) {
    documentIdState.toggleValue(documentId, false)
    validationOverlay.open()
  }

  const { documentFilters, tableColumnFiltersController, tableCuratedFilters } =
    useDocumentFiltersContext()
  const { selectedGrids, selectedGridFields } = useSelectedGridsContext()
  const { selectedView, isLoading: viewsIsLoading } = useViewsContext()

  const [highlightedValues] = useDragonFillContext()

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const [dialogDocumentId, setDialogDocumentId] = useState<string | null>(null)
  const documentDialogOverlay = useOverlay()

  const contextMenuOverlay = useOverlay()
  const contextMenu = useContextMenuPosition()

  const {
    columnFilters,
    columnFilterFns,
    filteringLogic,
    setColumnFilters,
    setColumnFilterFns,
  } = tableColumnFiltersController

  const sortingLogic = useColumnSortingLogic({
    fields: selectedGridFields,
    baseGridId: baseGrid?.id,
    selectedGrids,
  })

  const queryGrids = useMemo(() => {
    const tempQueryGrids = Object.keys(selectedGrids)
    if (
      baseGrid?.id &&
      (tempQueryGrids.length === 0 ||
        (!tempQueryGrids.includes(baseGrid.id) &&
          !filteringLogic.fieldType &&
          !sortingLogic.fieldType))
    ) {
      tempQueryGrids.push(baseGrid.id)
    }
    return tempQueryGrids
  }, [
    baseGrid?.id,
    filteringLogic.fieldType,
    selectedGrids,
    sortingLogic.fieldType,
  ])

  const commonOptions = {
    ordering: sortingLogic.fieldType ? sortingLogic.columnIds : undefined,
    ...tableCuratedFilters,
  }

  const {
    allStatesDocumentsCount,
    filteredDocumentsCount,
    idleDocumentsCount,
    inFlightDocumentsCount,
    rows,
    rowsUniqueDocIds,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isRefetching,
    isLoading: rowsIsLoading,
    queryKey,
    refetch,
  } = useDocumentGrid({
    projectGridIds: queryGrids,
    queryBy,
    enabled: !viewsIsLoading,
    countEnabled: true,
    ...commonOptions,
  })

  const { documents: documentsIds, isFetchingAll } = useDocumentDashboardIds({
    ...commonOptions,
    enabled: selectedAll,
  })

  const totalDocumentCount = tableCuratedFilters.workflowStateId
    ? idleDocumentsCount + inFlightDocumentsCount
    : allStatesDocumentsCount

  const {
    documentWorkflowStates = [],
    refetch: refetchDocumentWorkflowStates,
  } = useGetDocumentWorkflowStates({
    filters: {
      limit: '1000',
      workflow_state_id: state?.id,
      status__in: 'error,exporting',
    },
    enabled: state?.code === 'ready_for_export',
  })

  const throttledRefetchDocumentWorkflowStates = throttle(
    refetchDocumentWorkflowStates,
    1000,
  )

  const updateStateTableCallback = useCallback(
    ({ action }: WebsocketData) => {
      if (action === 'export_complete') {
        queryClient.invalidateQueries({
          queryKey: ['export_files', { projectId: project.id }],
        })
        refetch()
      } else if (
        // When documents move from one state to another
        action === 'document_workflow_state_changed' ||
        action === 'pdWorkflow/events.document_idle'
      ) {
        queryClient.invalidateQueries(
          { queryKey: queryKeys.documentRows.all },
          { cancelRefetch: false },
        )
        queryClient.invalidateQueries(
          { queryKey: queryKeys.documents.all },
          { cancelRefetch: false },
        )
        state?.code === 'ready_for_export' &&
          throttledRefetchDocumentWorkflowStates()
      } else if (
        // Document started exporting, has a failure, etc.
        action === 'document_workflow_state_status_changed' &&
        state?.code === 'ready_for_export'
      ) {
        throttledRefetchDocumentWorkflowStates()
      }
    },
    [
      project.id,
      queryClient,
      refetch,
      state?.code,
      throttledRefetchDocumentWorkflowStates,
    ],
  )

  useNotifications({
    keys: [state?.id],
    callback: (action) => {
      updateStateTableCallback(action)
    },
  })

  const columnsIds = useMemo(() => {
    const customColumnsIds = columns.map(({ id }) => id as string)
    const rowOrBaseColIndex = customColumnsIds.findIndex(
      (id) => id === 'rowOrBaseId',
    )
    if (rowOrBaseColIndex === -1) {
      customColumnsIds.splice(1, 0, 'rowOrBaseId')
    }
    return ['mrt-row-select', ...customColumnsIds]
  }, [columns])

  const selectedLoadedDocumentIds = [
    ...new Set(Object.keys(selectedRows).map((rowId) => rowId.split('_')[0])),
  ]

  const selectedDocumentIds =
    selectedAll && !isFetchingAll
      ? documentsIds.map(({ id }) => id)
      : selectedLoadedDocumentIds
  const columnsLengthRef = useRef({ old: columnsIds.length })
  const gridsRef = useRef({ old: project.project_grids })
  const viewRef = useRef({ old: selectedView.id })

  const validationNavListQuery = useValidationNavList({
    currentDocumentId: documentIdState.value,
    documentIds: useOnlySelected ? selectedDocumentIds : rowsUniqueDocIds,
    setDocumentId: documentIdState.toggleValue,
    rowsCount: rows.length,
    fetchNextPage:
      !useOnlySelected && hasNextPage && !isFetching && validationOverlay.isOpen
        ? fetchNextPage
        : undefined,
    onClose: () => {
      validationOverlay.close()
      // clear doc param when closing overlay
      documentIdState.resetValue(false)
      refetch()
      setUseOnlySelected(false)
    },
  })

  const { fetchMoreOnBottomReached } = useFetchMoreOnBottomReached({
    isFetching,
    hasNextPage,
    tableContainerRef,
    fetchNextPage,
    offset: 500,
  })

  const updateColumnsForViews = useCallback(
    (isViewChange = false) => {
      setColumnOrder((prev = []) => {
        const newOrder = isViewChange
          ? selectedView.state.column_order
          : [...prev]
        const newColumns = columnsIds.filter(
          (columnId) => !newOrder.includes(columnId),
        )
        return [...newOrder, ...newColumns]
      })
    },
    [columnsIds, selectedView.state.column_order, setColumnOrder],
  )

  const getThemeColor = useGetThemeColor()

  function getIsExporting(documentId: string) {
    if (!document) return false
    const documentWorkflowState = documentWorkflowStates.find(
      ({ document_id }) => documentId === document_id,
    )
    return documentWorkflowState?.status === 'exporting'
  }

  const resetSelection = useCallback(() => {
    setSelectedRows({})
    setSelectedAll(false)
    setSelectedAllLoaded(false)
  }, [])

  // Column visibility will affect sorting and filtering, so we need a way to check that they are really changing
  const sortingRef = useRef(sorting)
  const columnFiltersRef = useRef(columnFilters)

  useEffect(() => {
    if (JSON.stringify(sorting) !== JSON.stringify(sortingRef.current)) {
      resetSelection()
      sortingRef.current = sorting
    }
  }, [resetSelection, sorting])

  useEffect(() => {
    if (
      JSON.stringify(columnFilters) !== JSON.stringify(columnFiltersRef.current)
    ) {
      resetSelection()
      columnFiltersRef.current = columnFilters
    }
  }, [columnFilters, resetSelection])

  useEffect(() => {
    resetSelection()
  }, [
    state?.id,
    documentFilters?.flagCodes,
    documentFilters?.orgIds,
    documentFilters?.teamIds,
    resetSelection,
  ])

  // This is used for when selected rows get moved or deleted on a different page or tab, so
  // selected rows can get updated with 'actual' rows
  useEffect(() => {
    if (!Object.keys(selectedRows).length) return

    const rowsIds = rows.map(({ document_id, id, project_grid_id }) =>
      queryBy === 'documents' || project_grid_id === baseGrid?.id
        ? document_id
        : `${document_id}_${id}`,
    )
    const unselectedIds = Object.keys(selectedRows).filter(
      (id) => !rowsIds.includes(id),
    )

    if (unselectedIds.length) {
      setSelectedAll(false)
      setSelectedAllLoaded(false)
      setSelectedRows((prev) => {
        if (!Object.keys(prev).length) return prev
        const updatedRows = { ...prev }
        unselectedIds.forEach((id) => {
          delete updatedRows[id]
        })
        return updatedRows
      })
    } else if (
      // This is for when user keeps scrolling to fetch more data and had previously selected "Select All"
      selectedAll &&
      rowsIds.length > Object.keys(selectedRows).length
    ) {
      setSelectedRows(rowsIds.reduce((acc, id) => ({ ...acc, [id]: true }), {}))
    }
  }, [baseGrid?.id, queryBy, rows, selectedAll, selectedRows])

  useEffect(() => {
    if (viewRef.current.old !== selectedView.id) {
      resetSelection()
      if (selectedView.id === 'default') {
        setColumnOrder([])
      } else {
        updateColumnsForViews(true)
      }
      resetColumnSorting()
      resetColumnVisibility()
      viewRef.current.old = selectedView.id
    }
  }, [
    resetColumnSorting,
    resetColumnVisibility,
    resetSelection,
    selectedView.id,
    selectedView.state.column_visibility,
    selectedView.state.sorting,
    setColumnOrder,
    updateColumnsForViews,
  ])

  useEffect(() => {
    const previousLength = columnsLengthRef.current.old
    const currentLength = columnsIds.length

    const previousGrids = gridsRef.current.old
    if (project.project_grids !== previousGrids) {
      if (selectedView.id === 'default') {
        setColumnOrder(columnsIds)
      } else {
        updateColumnsForViews()
      }
      gridsRef.current.old = project.project_grids
      columnsLengthRef.current.old = currentLength
    } else if (previousLength < currentLength) {
      if (selectedView.id === 'default') {
        setColumnOrder((prev = []) =>
          prev.length
            ? [...prev, ...columnsIds.slice(previousLength)]
            : columnsIds,
        )
      } else {
        updateColumnsForViews()
      }
      columnsLengthRef.current.old = currentLength
    }
  }, [
    columnsIds,
    project.project_grids,
    selectedView.id,
    setColumnOrder,
    updateColumnsForViews,
  ])

  useEffect(() => {
    const visibleColumns = Object.entries(columnVisibility)
      .filter(([, visible]) => visible)
      .map(([id]) => id)
    // We remove field from sorting if user hides it, except for the ones in the selected view
    if (sorting.length > 0) {
      const viewSorting = (selectedView.state.sorting || []).map(({ id }) => id)
      setSorting((prevSorting) =>
        prevSorting.filter(
          ({ id }) => viewSorting.includes(id) || visibleColumns.includes(id),
        ),
      )
    }
    // We remove field from filter if user hides it
    if (columnFilters.length > 0) {
      setColumnFilters((prevColumnFilters) =>
        prevColumnFilters.filter(({ id }) => visibleColumns.includes(id)),
      )
    }
  }, [
    columnFilters.length,
    columnVisibility,
    selectedView.state.sorting,
    setColumnFilters,
    setSorting,
    sorting.length,
  ])

  const showSelectAllBanner =
    selectedAll ||
    (selectedAllLoaded &&
      filteredDocumentsCount > selectedLoadedDocumentIds.length)

  const allSelectedBannerHeight = showSelectAllBanner ? 45 : 0

  useEffect(() => {
    if (!Object.keys(selectedRows).length) {
      selectedAll && setSelectedAll(false)
      selectedAllLoaded && setSelectedAllLoaded(false)
    }
  }, [rows.length, selectedAll, selectedAllLoaded, selectedRows])

  // We add selected state here so memoMode can be 'rows' and we can use the selected state still.
  // Without this, the checkboxes don't update when "select all" is clicked.
  // Column Visibility is a dependency here so that when a column is hidden, the rows are updated.
  // Column Pinning is a dependency here so we update the rows whenever a column is pinned.
  const memoedRows = useMemo(() => {
    return [...rows]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selectedRows, columnVisibility, columnOrder, columnPinning])

  const table = useZerapixTable<Required<DocumentRow>>({
    columns,
    data: memoedRows as Required<DocumentRow>[],
    memoMode: 'rows',
    displayColumnDefOptions: {
      'mrt-row-select': {
        muiTableBodyCellProps: ({ row }) => {
          return rowsIsLoading ||
            (!state && !row.original.document?.document_workflow_states)
            ? {}
            : {
                sx: {
                  '& .MuiButtonBase-root::before': {
                    content: '""',
                    transition: 'all 0.1s',
                    width: 4,
                    background: state
                      ? getThemeColor(state.color)
                      : getThemeColor(
                          getRelevantWorkflowState(row.original.document)
                            ?.color,
                        ),
                    display: 'inline-block',
                    height: ROW_HEIGHT,
                    position: 'absolute',
                    left: -8,
                    zIndex: 2,
                  },
                },
              }
        },
      },
    },
    enableColumnDragging: true,
    enableColumnFilters: true,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    enableColumnActions: true,
    enableColumnPinning: true,
    enableDensityToggle: false,
    enableFilters: true,
    enableFilterMatchHighlighting: false, // Disabling to be consistent since highlight doesn't work for the fields values
    enableHiding: false,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableRowVirtualization: rows.length > 0, // Only virtualize after loading rows so the virtualizer doesn't try to use the skeletons heights for the rows
    enableSorting: true,
    enableStickyHeader: true,
    enableToolbarInternalActions: true,
    manualFiltering: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onColumnFilterFnsChange: setColumnFilterFns,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnSizingChange: setColumnSizing,
    onDraggingColumnChange: setDraggingColumn,
    onSortingChange: handleSortingChange,
    onIsFullScreenChange: setIsFullscreen,
    rowVirtualizerOptions: { overscan: 10, estimateSize: () => ROW_HEIGHT },
    muiSelectAllCheckboxProps: {
      // We don't use onChange because that would override onRowSelectionChange
      onClick: (event) => {
        const isChecked = (event.target as HTMLInputElement).checked
        setSelectedAllLoaded(isChecked)
        if (!isChecked && selectedAll) {
          setSelectedAll(false)
        }
      },
    },
    muiSelectCheckboxProps: {
      // We don't use onChange because that would override onRowSelectionChange
      onClick: (event) => {
        const isChecked = (event.target as HTMLInputElement).checked
        if (!isChecked && selectedAllLoaded) {
          setSelectedAllLoaded(false)
          selectedAll && setSelectedAll(false)
        }
      },
    },
    onRowSelectionChange: setSelectedRows,
    getRowId: (originalRow) => {
      return queryBy === 'documents' ||
        originalRow.project_grid_id === baseGrid?.id
        ? originalRow.document_id
        : originalRow.document_id
        ? `${originalRow.document_id}_${originalRow.id}`
        : originalRow.id
    },
    muiTablePaperProps: () => ({
      style: {
        zIndex: isFullscreen ? theme.zIndex.drawer + 100 : undefined, // using sx won't work https://www.material-react-table.com/docs/guides/full-screen-toggle#change-z-index-of-full-screen-table
      },
    }),
    muiTableHeadCellProps: () => {
      return {
        sx: {
          // Cut the header off with an ellipsis if it is too long
          '& .Mui-TableHeadCell-Content-Wrapper': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
        },
      }
    },
    muiTableBodyCellProps: ({ column }) => {
      const meta = column.columnDef.meta || {}

      const {
        // @ts-expect-error -- Not worth typing
        columnSubGroupPosition,
        // columnType, TODO: We used to use this to add a specific style for the data lineage columns. (Removing backdrop filter for not [sc-10883])
        // @ts-expect-error -- Not worth typing
        subGroupIds = [],
        // @ts-expect-error -- Not worth typing
        field,
      } = meta
      const isGroupVisible = subGroupIds.some(
        (id: string) => columnVisibility[id],
      )
      const groupBorder: Record<string, string> = {}
      if (columnSubGroupPosition === 'first') {
        groupBorder.borderLeft = `solid 2px ${theme.palette.divider}`
      } else if (isGroupVisible && columnSubGroupPosition === 'last') {
        groupBorder.borderRight = `solid 2px ${theme.palette.divider}`
      }
      return {
        sx: {
          ...groupBorder,
          py: field?.project_grid_field_type.code === 'picker' ? 0 : 1,
          overflow:
            field?.project_grid_field_type.code === 'picker'
              ? 'visible'
              : 'hidden',
        },
      }
    },
    muiTableBodyRowProps: ({ row }) => {
      const isExporting = getIsExporting(row.original.document_id)
      return {
        'data-testid': 'dg-row',
        onClick: () => {
          if (isExporting || highlightedValues.length > 0) return
          if (!rowsIsLoading) {
            if (state) {
              openValidationOverlay(row.original.document?.id)
            } else {
              window.open(
                `${window.location.pathname}?document_id=${row.original.document_id}`,
                '_blank',
              )
            }
          }
        },
        onContextMenu: (event) => {
          event.preventDefault()
          contextMenu.handleUpdatePosition(event.clientX, event.clientY)
          setDialogDocumentId(row.original.document_id)
          contextMenuOverlay.open(event)
        },
        sx: {
          cursor: rowsIsLoading || isExporting ? 'default' : 'pointer',
          filter: isExporting ? 'brightness(0.7)' : 'initial',
          height: ROW_HEIGHT,
        },
      }
    },
    muiTableContainerProps: {
      // @ts-expect-error -- wait for mui to update
      'data-testid': 'dg-table',
      ref: tableContainerRef, //get access to the table container element
      sx: {
        height: isFullscreen
          ? 'auto'
          : `calc(100vh - ${160 + allSelectedBannerHeight}px)`,
      },
      onScroll: (
        event, //add an event listener to the table container element
      ) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    muiTableFooterProps: {
      // @ts-expect-error -- wait for mui to update
      'data-testid': 'dg-footer',
    },
    muiColumnDragHandleProps: {
      id: 'table-handle',
    },
    renderTopToolbar: ({ table }) => {
      return (
        <Box
          p={1}
          sx={{
            background: theme.palette.background.paper,
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ px: 1, width: '100%' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">
                  {/* title */}
                  {queryBy === 'rows' ? 'Document Data' : 'Documents'}{' '}
                  <Typography
                    component="span"
                    color="text.secondary"
                    variant="h6"
                  >
                    (
                    {!rowsIsLoading &&
                    filteredDocumentsCount < totalDocumentCount
                      ? `${filteredDocumentsCount}/${totalDocumentCount}`
                      : totalDocumentCount}
                    )
                  </Typography>
                </Typography>
                {state?.description && (
                  <>
                    <Tooltip
                      title="View Documentation"
                      placement="right"
                      enterDelay={1000}
                    >
                      <IconButton
                        size="small"
                        className="description-button"
                        onClick={(e) => {
                          e.stopPropagation()
                          documentationOverlay.open(e)
                        }}
                      >
                        <Description fontSize="inherit" />
                      </IconButton>
                    </Tooltip>

                    <WorkflowStateDescriptionDialog
                      overlay={documentationOverlay}
                      projectId={project.id}
                      workflowState={state}
                    />
                  </>
                )}
              </Stack>

              {/* renderTableActions */}
              {state && (
                <>
                  <InFlightDocumentsDialog
                    count={inFlightDocumentsCount}
                    openValidationOverlay={openValidationOverlay}
                    stateId={state.id}
                  />
                  <DocumentGridActionButtons
                    listQueryKey={queryKey}
                    queryBy={queryBy}
                    workflow={workflow}
                    state={state}
                    selectedDocumentIds={selectedDocumentIds}
                    tableHasData={!!rows.length}
                    setSelectedRows={setSelectedRows}
                    onViewSelected={() => {
                      setUseOnlySelected(true)
                      openValidationOverlay(selectedDocumentIds[0])
                    }}
                  />
                </>
              )}
            </Stack>
            {/* renderToolbarInternalActions */}
            <Stack
              data-testid="dg-internal-actions"
              direction="row"
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <DocumentGridActionsMenu
                dataQueryKey={queryKey}
                selectedDocumentIds={selectedDocumentIds}
                queryBy={queryBy}
                setSelectedRows={setSelectedRows}
                refetchDocuments={refetch}
              />
              <MRT_ToggleFiltersButton table={table} />
              <MRT_ToggleFullScreenButton table={table} />
            </Stack>
          </Stack>
          {showSelectAllBanner && (
            <SelectAllBanner
              loadedDocumentsCount={selectedLoadedDocumentIds.length}
              isFetchingDocs={isFetchingAll}
              selectedAll={selectedAll}
              setSelectedAll={setSelectedAll}
              setSelectedAllLoaded={setSelectedAllLoaded}
              setSelectedRows={setSelectedRows}
              totalDocumentsCount={filteredDocumentsCount}
            />
          )}
          <LinearProgress
            sx={{
              visibility: isFetching && !isRefetching ? 'visible' : 'hidden',
            }}
          />
        </Box>
      )
    },
    state: {
      columnFilters,
      columnFilterFns,
      columnOrder: columnOrder.length ? columnOrder : columnsIds,
      columnPinning,
      columnSizing,
      columnVisibility,
      density: 'compact',
      draggingColumn,
      isFullScreen: isFullscreen,
      rowSelection: selectedRows,
      showSkeletons: !isFullscreen && (viewsIsLoading || rowsIsLoading),
      sorting,
    },
    muiSkeletonProps: {
      animation: 'pulse',
    },
    positionToolbarAlertBanner: 'bottom',
    onColumnPinningChange: setColumnPinning,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    localization: {
      noRecordsToDisplay: 'Drag and drop files here to upload them.',
      toggleSelectRow: '',
    },
  })

  return (
    <>
      <ZerapixTable<Required<DocumentRow>> table={table} />
      <ValidationOverlay
        overlay={validationOverlay}
        documentsCount={
          useOnlySelected ? selectedDocumentIds.length : filteredDocumentsCount
        }
        documentIdState={documentIdState}
        refetchTableData={refetch}
        setUseOnlySelected={setUseOnlySelected}
        {...validationNavListQuery}
      />
      {dialogDocumentId && (
        <>
          <DocumentGridRowMenu
            overlay={contextMenuOverlay}
            position={contextMenu.position}
            documentId={dialogDocumentId}
            projectId={project.id}
            onOpenDocumentDialog={() => {
              documentDialogOverlay.open()
            }}
            onDelete={() => {
              setDialogDocumentId(null)
            }}
          />

          <DocumentDialog
            documentId={dialogDocumentId}
            overlay={documentDialogOverlay}
            projectId={project.id}
            onBeforeDelete={() => {
              setDialogDocumentId(null)
            }}
          />
        </>
      )}
    </>
  )
}

const MemoizedDashboardTable = memo(DashboardTable)

export function DocumentTable(props: BaseDashboardTableProps) {
  const { state } = props

  const columns = useDocumentColumns({
    workflowState: state,
  })

  return (
    <DragonFillProvider>
      <MemoizedDashboardTable
        columns={columns}
        queryBy="documents"
        {...props}
      />
    </DragonFillProvider>
  )
}

export function DocumentRowsTable(props: BaseDashboardTableProps) {
  const { state, workflow } = props
  const columns = useDocumentRowsColumns({
    workflowState: state,
    workflow,
  })

  return (
    <DragonFillProvider>
      <MemoizedDashboardTable columns={columns} queryBy="rows" {...props} />
    </DragonFillProvider>
  )
}
